import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import type { Scene } from '@babylonjs/core/scene';

import type { NodeTypeDto, RoomCapacityDto } from '../generated/contracts';
import { cloneModelTemplate } from '../room/ModelLibrary';
import { buildSilhouette } from '../room/Silhouettes';
import { Carousel, CAROUSEL_HIGHLIGHT_FORWARD, CAROUSEL_HIGHLIGHT_SCALE } from './Carousel';
import { buildLabelledPlate, disposePlate } from './LabelledPlate';
import type { TrayWheelItem } from './TrayWheel';
import type { Grabber, GrabberHub } from './Grabbers';
import type { ReadoutOption, ReadoutSwatch } from './ModeDefinition';
import { NodeKind, type NodeKindValue } from '../net/wire';

/**
 * The tray: the off hand presents it, the dominant hand takes from it.
 *
 * It is the whole menu, and it is contextual — empty-handed it offers things to place, holding a
 * node it offers that node's actions, and in draw mode it offers ink. One gesture to learn.
 *
 * Two kinds of content, not one. A **wheel** — node categories, and ink colours plus the eraser
 * — is a *setting*: a value you are choosing among, where re-choosing what you already have is a
 * no-op, and it applies live as it spins, with no commit (plan/tray-categories.md, "the wheel
 * carries settings"). A **row or grid** — node types — is a *commit*: it creates a node, and it
 * stays behind the trigger/click every commit in this tray always needed. The wheel
 * replaced a flat row of chips (categories) and a flat row of colours (ink) once nine categories
 * made the row's own reach a metre wide at the edges; see plan/tray-categories.md for the full
 * case, including the two rejected designs (a second row, a radial dial) it argues past.
 *
 * Selecting from it is not one mechanism. A controller's off hand owns the tray by stick once
 * it is open: the stick moves a highlight through rows and grids, and spins a wheel left/right
 * while the highlight sits on one. The desktop pointer clicks a slot or action, and
 * hovers-and-scrolls a wheel, or clicks a flanking item to rotate it to the front. Both read
 * the same wheel state (`TrayWheel`); none of it is duplicated per input. (Tracked hands used
 * to be a third input here — reach-to-dwell on a slot, or pinch-and-drag a wheel's front item —
 * removed outright, 2026-08-10; see CLAUDE.md's "Grip means near, trigger means far" entry.)
 *
 * The types come from the catalogue fetched at boot, not from a list here, so the tray can never
 * offer something the server would refuse to place.
 */

/**
 * What the tray is showing, decided by the current mode (`ModeDefinition.offHandSurface`,
 * plan/mode-hierarchy.md — "the off-hand surface comes from the mode"). `place` is Build's own
 * interactive shape grid, still anchored to the off hand; `readout` is every other mode's
 * passive display of whatever the dominant stick's own axes are currently set to (draw's
 * ink/marker, scan's line length, link's own style/arrow) — see `AuthoringController
 * .offHandContext`, the one place that reads `ModeDefinition.offHandSurface` to decide which of
 * these two a given tick shows. `offHandSurface`'s own name is now a partial misnomer for
 * `readout`: the *field* still names the surface every non-Build mode used to show on the off
 * hand, but `readout` itself renders anchored to the *dominant* hand today (`updateVisibility`/
 * `updateReadoutVisibility`, below) — the hand that actually operates the axis it reflects
 * (plan/mode-hierarchy.md, "A readout belongs on the hand that chooses it"). Only `place` is
 * still, literally, off-hand.
 *
 * There used to be a third, `edge` — Link's own action row, shown once its trigger had aimed at
 * an edge, offering a single action, `label`, that opened the (now-deleted) in-VR note editor
 * (plan/ui-improvements.md, item 4). Removed alongside that editor: labelling an edge has no
 * remaining in-headset entry point at all (a node's own label is reached through speech-to-text
 * instead — `NodeLabelController` — which does not generalise to an edge; see that deletion's own
 * PR for the full account of why). Aiming at an edge in Link mode still selects it
 * (`AuthoringController.selectEdge`, `ConnectionDrawer.onEdgeAimed`) and still thickens it in the
 * scene (`RoomView.selectConnection`) — that is a plain visual affordance, not tray content, and
 * outlived the action row that used to sit beside it. With no action row left to show for a
 * selected edge, Link mode's off-hand surface is `readout` unconditionally now, selected or not.
 *
 * There used to be a fourth, `act` — the held-node context, `edit text · duplicate · delete` —
 * removed outright (plan/ui-improvements.md, item 2: the owner, "we don't need an explicit edit
 * text action or really duplicate on the left hand when grabbing a node. I'd rather have the
 * left hand turn into a trash can to act as a delete button"). `duplicate` has no replacement;
 * `edit text` is reached through speech-to-text instead; `delete` becomes
 * `TrashCanRenderer`/`PossessionManager.release`'s own bin gesture. `AuthoringController` also
 * stops the tray from opening at all while a node is held (`openSuppressed`), since there is
 * nothing left in this context to show.
 *
 * `ink` used to be draw mode's own context — an interactive ink wheel plus a "put-away" row —
 * superseded here by `readout`: mode switching moved onto the dominant grip+stick
 * (plan/mode-hierarchy.md), which is what "put-away" existed to do by hand, and colour/width
 * moved onto the dominant stick's own bare axes, which is what the ink wheel existed to offer
 * from the off hand. Neither has anywhere left to be an off-hand *action*; `readout` shows the
 * result instead.
 */
export type TrayContext = 'place' | 'readout';

/** Which kind of surface the tray is currently presenting — `'palette'` for `place`
 *  (interactive: the off-hand stick navigates it), `'readout'` for everything else (passive —
 *  the off-hand stick is never repurposed while one is showing). Exposed as `PaletteUI
 *  .surfaceKind` so `main.ts` can gate `XrManager.travelSuppressed` on it directly, rather than
 *  on `isOpen` alone (plan/mode-hierarchy.md: "still needed, but now only in Build/Place"). */
export type TraySurfaceKind = 'palette' | 'readout';

const CATEGORY_LABELS: Record<string, string> = {
  primitives: 'shapes',
};

/** The category wheel's own plate colours — categories carry no colour of their own (unlike a
 *  node type or an ink colour), so every wheel item shares the same chip-like background this
 *  tray has always used for a category. */
const CATEGORY_PLATE_BACKGROUND = '#2B313B';
const CATEGORY_PLATE_INK = '#E7ECF3';

/** Miniatures are small enough that the tray does not swallow the room behind it. */
const MINI_SCALE = 0.24;
const SLOT_SPACING = 0.1;
const ROW_SPACING = 0.095;

/** Past this a row is wider than a comfortable reach, so it wraps instead. */
const SLOTS_PER_ROW = 5;

/**
 * How far the *lowest* row sits above the presenting hand.
 *
 * Measured from the bottom of the tray rather than from its middle, deliberately. Rows are laid
 * out upwards from here, so adding a second row grows the tray away from the hand instead of
 * down into it — which is what put the slots on top of the hand the first time.
 */
const HAND_CLEARANCE = 0.15;

/** A category's own plate on the wheel. */
const WHEEL_ITEM_WIDTH = 0.1;
/** plan/tray-categories.md: "about 0.012 m [corner radius] on a 0.05 m item." */
const WHEEL_ITEM_HEIGHT = 0.05;

/** Clearance between the wheel's front position and the grid or row below it. */
const WHEEL_GAP = 0.04;

/** Still used for the capacity warning and readout plates' own height, even though the action
 *  row that originally defined it (the edge label action) is gone. */
const ACTION_HEIGHT = 0.06;

/**
 * The room-capacity readout, shown in `place` context once the live node count is within
 * `RoomCapacityDto.warningMargin` of `maxNodes` — plan/open-items.md, "A hard cap of 100 nodes
 * per room": "The tray should show the limit approaching as nodes accumulate, not simply
 * refuse creation at node 101 with no warning beforehand." Sits above the wheel — the topmost
 * row `place` context has left, now that mode switching no longer needs a "pen" item to reach
 * draw mode by hand (plan/mode-hierarchy.md).
 */
const CAPACITY_WARNING_GAP = 0.03;
const CAPACITY_WARNING_WIDTH = 0.22;
const CAPACITY_WARNING_HEIGHT = ACTION_HEIGHT;

/** A readout line's own plate size — a passive display, so narrower and shorter than an action
 *  row's commit-sized plate reads correctly as "nothing to press here." Stacked vertically,
 *  `READOUT_LINE_SPACING` apart, centred on the tray's own anchor. */
const READOUT_LINE_WIDTH = 0.22;
const READOUT_LINE_HEIGHT = ACTION_HEIGHT;
const READOUT_LINE_SPACING = 0.07;
const READOUT_BACKGROUND = '#12151aE6';
const READOUT_INK = '#E7ECF3';

/** How close the dominant hand must be to a slot to reach it. */
const PICK_REACH = 0.06;

/**
 * How long `flashReadout()` keeps the tray open for, from the moment of the change it is
 * reporting — originally the Link-carousel-visibility fix: the carousel (PR #153) shipped only
 * ever showing while the off-hand grip was held, but Link's own style/arrow cycle lives on the
 * *dominant* stick, a hand nobody was told to also grip — so a headset session cycling it with
 * the dominant stick alone, off-hand grip untouched, saw nothing (owner: "no link mode carousel
 * when I used the right stick while in carousel mode"). A later headset pass moved the readout
 * itself onto the dominant hand (plan/mode-hierarchy.md, "A readout belongs on the hand that
 * chooses it"), so this is now the *only* way any of Draw/Scan/Link's readout ever opens — there
 * is no grip-hold for it to fall back to any more (see `updateReadoutVisibility`'s own doc).
 * Long enough to read two short lines and glance at a carousel item ("style: dashed"), short
 * enough that it reads as a nudge rather than a menu that forgot to close — the same rough order
 * of magnitude a toast/status flash uses elsewhere in this project. Cuts rather than fades: the
 * tray already disappears the same abrupt way the instant the off-hand grip releases in Build,
 * and a flash is that identical disappearance on a timer instead of a button, not a new visual
 * language.
 */
export const TRANSIENT_READOUT_DURATION_MS = 1500;

/**
 * The desktop pointer's hover/click reach against any visible wheel item — literally the old
 * chip row's `CHIP_REACH`, reused rather than a new number: plan/tray-categories.md, "on the
 * order of 0.1 m: `Vector3.Distance` against a mesh position, which is exactly the test
 * `CHIP_REACH` already is."
 */
const WHEEL_REACH = 0.07;

/**
 * Off-hand stick navigation, while the tray is open.
 *
 * Controller only — `Grabber.stick` is null for the desktop pointer, which has neither a
 * stick nor a trigger, so none of this runs for it and it keeps the reach-based mechanisms
 * above exactly as they always worked.
 */

/**
 * Discrete stepping: push past ENGAGE to move one cell, and the stick must fall back under
 * RELEASE before it can move again. Exported: `AuthoringController`'s dominant-hand mode wheel
 * (plan/history/control-modes.md, "the right stick becomes a mode wheel") reuses these exact numbers
 * rather than a second set — "same gesture at any tool count," and the same feel this stick
 * already has for tray navigation and for `XrManager`'s own snap-turn.
 */
export const STEP_ENGAGE = 0.7;
export const STEP_RELEASE = 0.3;

/** How much bigger, and how far forward, the currently highlighted place-context cell reads.
 *  Slots and action rows use it on a hover/highlight. The wheel's own front item uses the
 *  identical numbers unconditionally, re-exported from `Carousel.ts` as
 *  `CAROUSEL_HIGHLIGHT_SCALE`/`CAROUSEL_HIGHLIGHT_FORWARD` rather than redefined here — a grid
 *  slot's "about to be chosen" hover and a wheel's "this is the one, always" front item are
 *  different states, but the same visual language, and the tray is exactly the place a second,
 *  independently-tuned copy of "bigger and a touch closer" would start to drift from the first. */
const HIGHLIGHT_SCALE = CAROUSEL_HIGHLIGHT_SCALE;
const HIGHLIGHT_FORWARD = CAROUSEL_HIGHLIGHT_FORWARD;

/**
 * A readout's own colour swatches (`ModeDefinition.readoutSwatches`, plan/mode-hierarchy.md's
 * draw-variants task) — a small carousel of solid-colour plates, stacked above the text lines
 * rather than folded into them, since a colour is something to look at rather than a word to read
 * (the whole reason this exists: the old ink wheel showed a raw hex code instead). It is a
 * `Carousel` (`Carousel.ts`) like the tray's own category wheel and the mode ring — the owner's
 * own instruction to standardize on one carousel component everywhere rather than three visually
 * similar but separately-built rows (plan/ui-improvements.md). The selected swatch reads as "the
 * one" the same way every carousel's front item does — larger and slightly forward, by
 * construction, with no separate marker needed here.
 */
const SWATCH_SIZE = 0.045;
const SWATCH_ROW_GAP = 0.05;

/**
 * A readout's own labelled option carousel (`ModeDefinition.readoutOptionRows`, Link mode's
 * line-style/arrow-direction task) — one `Carousel` per row, stacked above the text lines the
 * same way the colour swatch carousel already is, and sized like the tray's own category wheel
 * items (`WHEEL_ITEM_WIDTH`/`WHEEL_ITEM_HEIGHT`) rather than the smaller colour swatches: these
 * chips carry real text ("dashed", "remove"), not a bare colour, and need the wheel's own width
 * to hold it. `OPTION_DANGER_BACKGROUND` reuses `TrashCanRenderer.BIN_COLOR` verbatim — the
 * project's own established "this deletes something" colour (also `DebugPanel.ts`'s reset
 * buttons) — rather than inventing a second red, so the `'remove'` position reads as the same
 * kind of danger the trash can already does, not a new one.
 */
const OPTION_ITEM_WIDTH = WHEEL_ITEM_WIDTH;
const OPTION_ITEM_HEIGHT = WHEEL_ITEM_HEIGHT;
const OPTION_ROW_GAP = SWATCH_ROW_GAP;
const OPTION_BACKGROUND = CATEGORY_PLATE_BACKGROUND;
const OPTION_DANGER_BACKGROUND = '#FF5D8F';
const OPTION_INK = '#FFFFFF';

export interface PaletteSelection {
  kind: NodeKindValue;
  shapeType?: string;
  color: string;

  /** The catalogue's own display name for this type ("Firewall", "Web server") —
   *  `PlacementManager.begin` uses this as the new node's default label. Optional because a
   *  selection can also come from outside the real palette (a test, mainly), which has no
   *  catalogue entry to read one off; `PlacementManager` falls back to no label in that case,
   *  same as before this existed. */
  displayName?: string;
}

interface Slot {
  mesh: Mesh;
  selection: PaletteSelection;
}

/** One step of the off-hand stick cursor, indexing into `navRows`. */
interface TrayCursor {
  row: number;
  col: number;
}

/**
 * What a navigable cell actually is, so firing it knows what to do. `wheel` carries no data of
 * its own — which wheel is meant is implied by context, and today that is only ever `place`'s
 * own category wheel, since `readout` is not navigable at all — because a row only ever has one
 * wheel cell in it, never two.
 */
type NavCell = { kind: 'wheel' } | { kind: 'slot'; category: string; index: number };

export class PaletteUI {
  private readonly root: TransformNode;
  private readonly placeGroup: TransformNode;
  private readonly readoutGroup: TransformNode;

  /** Slot rows by category. Only the active one is enabled. */
  private readonly categories = new Map<string, { row: TransformNode; slots: Slot[] }>();

  /** Node categories, as a carousel — plan/tray-categories.md. Live-applies as it spins. A
   *  `Carousel` (`Carousel.ts`), the same component the mode ring and the colour readout below
   *  both use — this is the original the other two were extracted from, not a copy of them. */
  private readonly categoryWheel: Carousel;
  private readonly categoryWheelAnchor: TransformNode;

  /**
   * The `readout` context's own plates — one per line `readoutLines()` returns, stacked
   * vertically. Purely a display: nothing here is stick-navigable or dwell-fireable (see
   * `TrayContext`'s own doc for why the ink wheel and "put-away" this replaces no longer have
   * anywhere to be an off-hand *action*). Rebuilt only when the lines actually change — the
   * same "compare the text, not the tick" rule `capacityWarningMesh` already follows — not on
   * every tick, which would rebuild several textures 60+ times a second for no visible reason.
   */
  private readoutMeshes: Mesh[] = [];
  private readoutText: readonly string[] = [];

  /**
   * The `readout` context's own colour swatches — a `Carousel`, the same component the category
   * wheel above and the mode ring both use, arced above the text lines rather than folded into
   * them. Content (`setItems`) is rebuilt only when the swatch set itself changes, and the
   * active item snapped (`snapTo`, no ease — see `Carousel.ts`'s own doc for why a readout that
   * only ever mirrors a decision already made elsewhere snaps rather than spins) whenever the
   * selected one changes, compared by a flattened `color:selected` key, the same "compare the
   * content, not the tick" rule `readoutMeshes` already follows for its own lines.
   */
  private readonly swatchCarousel: Carousel;
  private readonly swatchCarouselAnchor: TransformNode;
  private swatchKey = '';

  /**
   * The `readout` context's own labelled option carousels — Link mode's line-style/arrow-
   * direction rows (`ModeDefinition.readoutOptionRows`), one `Carousel` per row, stacked above
   * the swatch carousel/text lines. A pool rather than a fixed pair: grown lazily as
   * `readoutOptionRows()` ever asks for more rows than currently exist, and *hidden*, not
   * disposed, once fewer are asked for — the same "toggle visibility rather than rebuild" rule
   * `capacityWarningMesh` already follows, since switching between Link and another readout mode
   * while the tray stays open would otherwise churn meshes on every mode switch. Every mode but
   * Link returns zero rows, so this pool never grows past two in practice today.
   */
  private readonly optionRowCarousels: { anchor: TransformNode; carousel: Carousel; key: string }[] = [];

  /** The last value `setPickable` was called with — see that method's own doc for why this is
   *  remembered rather than only applied at call time, mirroring `Carousel`'s own identical
   *  field for the identical reason. */
  private pickableState = false;

  private activeCategoryKey = '';

  /** Where the wheel sits, once the tallest category is known — was `chipY`. */
  private wheelY = WHEEL_GAP;

  private open = false;
  private showing: TrayContext = 'place';

  /**
   * The deadline (a `performance.now()` timestamp) through which `updateVisibility` should keep
   * the tray open even though the off-hand grip is not held — set by `flashReadout()`, cleared
   * once it lapses or a genuine grip-hold takes over. `null` means no flash is pending, the same
   * "absence, not a stale zero" convention `capacityWarningText` uses for its own optional field.
   */
  private transientReadoutUntilMs: number | null = null;

  /**
   * The grid the off-hand stick steps through: rows of cells, top to bottom in the order they
   * are drawn. Rebuilt fresh every tick from whatever is actually showing.
   */
  private navRows: NavCell[][] = [];

  /** Where the stick cursor is. In `place` it starts on the wheel's own row — see
   *  `resetNavigation` and `clampCursor`. `readout` never has a cursor at all, since it has
   *  nothing to navigate. */
  private cursor: TrayCursor | null = null;

  /** Set by `resetNavigation`; resolved by `clampCursor` on the next `buildNavRows()`, since a
   *  reset can happen before this tick's rows exist yet (`updateVisibility` runs before
   *  `applyContext`). Deferring the lookup this way, rather than searching stale rows, is what
   *  keeps "opens on the wheel" correct regardless of which caller reset first this tick. */
  private cursorWantsWheelFocus = false;

  private stickArmed = true;

  /** `nowMs` of the previous tick, for the wheels' own visual ease — null until the first
   *  tick, so the very first frame does not ease from an unbounded elapsed time. */
  private lastTickMs: number | null = null;

  /**
   * Where the tray is anchored. A hand presents it and the *other* hand takes from it; a
   * fixed tray floats in front of the viewer and can be taken from by anything, which is
   * what the desktop pointer needs since it has no second hand to present with.
   */
  private anchor: { mode: 'hand'; handedness: XRHandedness } | { mode: 'fixed' } = {
    mode: 'hand',
    handedness: 'left',
  };

  /** What the tray should be showing. Set by the caller; decides the contents. */
  context: () => TrayContext = () => 'place';

  /**
   * Vetoes opening the hand-anchored tray outright — read every tick, alongside the off-hand
   * grip/palm-up check `updateVisibility` already makes. `AuthoringController` wires this to
   * "a node is currently held," since the tray context that used to open for a held node
   * (`edit text · duplicate · delete`) is gone (plan/ui-improvements.md, item 2) and there is
   * nothing left in `place`/`readout` worth showing while the off hand is busy being a
   * trash can (`TrashCanRenderer`) instead. Does not affect the *fixed* desktop tray (`openInFront`/
   * `toggleInFront`): desktop has no off hand and no trash can, so `Tab` keeps working exactly
   * as before, harmlessly — the dominant-hand reach guard on picking (`far.justPressed`, moved
   * off the grip alongside every other dominant-hand grab — plan/mode-hierarchy.md, settled
   * 2026-08-10) already stops a second item being taken while the mouse is busy holding one.
   */
  openSuppressed: () => boolean = () => false;

  /**
   * Looks up a model-backed type's preloaded template mesh, if one exists — buildMini clones it
   * for a slot's mini instead of falling back to the procedural silhouette, exactly the choice
   * NodeMeshFactory.createShape already makes for the real node. Defaulted to "no template ever
   * exists" so a test can construct a PaletteUI without wiring this, the same reasoning every
   * other optional caller-supplied hook on this class already follows.
   *
   * Unlike `context`/`readoutLines` (read every tick), this is read eagerly, inside `setCatalog`
   * — so it has to be assigned *before* `setCatalog` is first called, not merely before the
   * tray is opened. `AuthoringController` does that: it wires this immediately after
   * constructing the palette, and only then does main.ts call `setCatalog`.
   */
  modelTemplateFor: (key: string) => Mesh | undefined = () => undefined;

  /**
   * The room's live node count, read fresh every tick this class is showing `place` context —
   * never a value captured once, because the count this compares against the cap has to
   * reflect what the room actually holds right now, the same "not a cached client-side
   * number" reasoning `RoomMutationService.CreateNodeAsync`'s own enforcement follows.
   * Defaulted to zero so a test can construct a `PaletteUI` without wiring this, the same
   * reasoning every other optional caller-supplied hook on this class already follows.
   */
  nodeCount: () => number = () => 0;

  /**
   * The cap this room enforces, and the margin before it at which the tray should start
   * showing a count — `GET /api/room-capacity`, set once at boot via `setCapacity`. Null
   * (the default) means "not known yet" and hides the warning unconditionally, the same as
   * `capacity.warningMargin` never having been crossed.
   */
  private capacity: RoomCapacityDto | null = null;

  /**
   * Built lazily, the first time the room actually crosses the warning threshold — most rooms
   * never approach the cap, and a `DynamicTexture` plate nobody will ever see is a texture
   * nobody asked for. Rebuilt only when the text it shows changes (never on every tick, which
   * would rebuild a texture 60+ times a second for no visible reason).
   */
  private capacityWarningMesh: Mesh | null = null;
  private capacityWarningText: string | null = null;

  /**
   * The lines the `readout` context shows — read fresh every tick that context is showing, the
   * same "no cached value that can go stale" reasoning `nodeCount` already follows. Defaulted
   * to "nothing to show" so a test can construct a `PaletteUI` without wiring this.
   * `AuthoringController` wires it to the current `ModeDefinition.readoutLines()`
   * (plan/mode-hierarchy.md).
   */
  readoutLines: () => readonly string[] = () => [];

  /**
   * The colour swatches the `readout` context shows alongside `readoutLines` — read fresh every
   * tick that context is showing, the same "no cached value that can go stale" reasoning
   * `readoutLines`/`nodeCount` already follow. Defaulted to "nothing to show" so a test can
   * construct a `PaletteUI` without wiring this. `AuthoringController` wires it to the current
   * `ModeDefinition.readoutSwatches()` (plan/mode-hierarchy.md's draw-variants task).
   */
  readoutSwatches: () => readonly ReadoutSwatch[] = () => [];

  /**
   * Rows of a labelled option carousel the `readout` context shows alongside `readoutLines` —
   * read fresh every tick, the same "no cached value that can go stale" reasoning every other
   * readout hook here follows. Defaulted to "nothing to show" so a test can construct a
   * `PaletteUI` without wiring this. `AuthoringController` wires it to the current
   * `ModeDefinition.readoutOptionRows()` — Link mode's line-style/arrow-direction carousels,
   * plan/mode-hierarchy.md's carousel task.
   */
  readoutOptionRows: () => readonly (readonly ReadoutOption[])[] = () => [];

  /** Called when a miniature is taken. The caller turns it into a placement. */
  onPicked: ((selection: PaletteSelection, grabber: Grabber) => void) | null = null;

  constructor(
    private readonly scene: Scene,
    private readonly grabbers: GrabberHub,
  ) {
    this.root = new TransformNode('tray', scene);
    this.root.setEnabled(false);

    this.placeGroup = new TransformNode('tray-place', scene);
    this.placeGroup.parent = this.root;

    this.readoutGroup = new TransformNode('tray-readout', scene);
    this.readoutGroup.parent = this.root;
    this.readoutGroup.setEnabled(false);

    this.categoryWheelAnchor = new TransformNode('tray-category-wheel', scene);
    this.categoryWheelAnchor.parent = this.placeGroup;
    this.categoryWheel = new Carousel(scene, this.categoryWheelAnchor, {
      itemWidth: WHEEL_ITEM_WIDTH,
      itemHeight: WHEEL_ITEM_HEIGHT,
      ink: CATEGORY_PLATE_INK,
      meshNamePrefix: 'wheel',
    });

    this.swatchCarouselAnchor = new TransformNode('tray-readout-swatch-wheel', scene);
    this.swatchCarouselAnchor.parent = this.readoutGroup;
    this.swatchCarousel = new Carousel(scene, this.swatchCarouselAnchor, {
      itemWidth: SWATCH_SIZE,
      itemHeight: SWATCH_SIZE,
      ink: READOUT_INK,
      meshNamePrefix: 'tray-readout-swatch',
    });
  }

  get isOpen(): boolean {
    return this.open;
  }

  /** `'palette'` while showing `place` (interactive — the off-hand stick navigates it),
   *  `'readout'` otherwise. `main.ts` gates `XrManager.travelSuppressed` on `isOpen &&
   *  surfaceKind === 'palette'` rather than `isOpen` alone (plan/mode-hierarchy.md: "still
   *  needed, but now only in Build/Place... In Draw, Scan and Link the off-hand stick is never
   *  repurposed — it keeps doing locomotion throughout"). */
  get surfaceKind(): TraySurfaceKind {
    return this.showing === 'readout' ? 'readout' : 'palette';
  }

  /**
   * The room-capacity readout's own current text (e.g. "92 / 100 nodes"), or `null` while it
   * is hidden — below the warning threshold, in a context other than `place`, or with no
   * capacity known yet. Exposed so a test (`NullEngine` cannot render a pixel — CLAUDE.md,
   * "When nothing in the project can see a rendered pixel") can confirm the warning's
   * *presence*, which is the one thing headless coverage can actually check; its on-screen
   * appearance is unconfirmed until a headset session looks at it.
   */
  get capacityWarning(): string | null {
    return this.capacityWarningMesh?.isEnabled() ? this.capacityWarningText : null;
  }

  /** The category the grid is currently showing — the wheel's own front item. Exposed so a
   *  caller (or a test) can confirm opening or closing the tray never silently changed it. */
  get activeCategory(): string {
    return this.activeCategoryKey;
  }

  /** The shape-category `Carousel` itself — exposed only so a test can confirm this, the mode
   *  ring's, and the colour readout's carousels are the same `Carousel` class, structurally, not
   *  three separately-built rows that merely look alike (plan/ui-improvements.md, the owner's
   *  own instruction to extract one shared component "so it doesn't drift"). */
  get categoryCarousel(): Carousel {
    return this.categoryWheel;
  }

  /** The draw-mode colour readout's own `Carousel` — see `categoryCarousel`'s own doc. */
  get colorCarousel(): Carousel {
    return this.swatchCarousel;
  }

  /** Link mode's own line-style/arrow-direction carousels, one per row `readoutOptionRows()`
   *  currently returns — see `categoryCarousel`'s own doc for why this is exposed: confirms
   *  these are genuinely the same `Carousel` class as the category wheel and the colour readout,
   *  not a fourth, separately-built visual language. Empty until `updateReadoutOptions` has grown
   *  the pool at least once — see that method's own doc for why the pool is lazy. */
  get optionCarousels(): readonly Carousel[] {
    return this.optionRowCarousels.map((entry) => entry.carousel);
  }

  /** Builds the placeable half from the catalogue. Called once at boot. */
  setCatalog(types: NodeTypeDto[]): void {
    this.clearCategories();

    const byCategory = new Map<string, NodeTypeDto[]>();

    for (const type of types) {
      const group = byCategory.get(type.category) ?? [];
      group.push(type);
      byCategory.set(type.category, group);
    }

    for (const [category, entries] of byCategory) {
      this.addCategory(
        category,
        entries.map((type) => ({
          kind: NodeKind.Shape,
          shapeType: type.key,
          color: type.defaultColor ?? '#4C8DFF',
          silhouette: type.silhouette,
          displayName: type.displayName,
        })),
      );
    }

    // Above the tallest category, so the wheel does not jump as you switch between a category
    // that wraps onto two rows and one that does not.
    const tallest = Math.max(
      ...[...this.categories.values()].map((group) => Math.ceil(group.slots.length / SLOTS_PER_ROW)),
      1,
    );

    this.wheelY = (tallest - 1) * ROW_SPACING + WHEEL_GAP + WHEEL_ITEM_HEIGHT;
    this.categoryWheelAnchor.position.set(0, this.wheelY, 0);

    const previousKey = this.categoryWheel.activeItem?.key;
    const wheelItems: TrayWheelItem[] = [...this.categories.keys()].map((key) => ({
      key,
      label: CATEGORY_LABELS[key] ?? key,
      color: CATEGORY_PLATE_BACKGROUND,
    }));

    this.categoryWheel.setItems(wheelItems, previousKey);

    this.showCategory(this.categoryWheel.activeItem?.key ?? '');

    if (this.capacityWarningMesh) {
      this.positionCapacityWarning(this.capacityWarningMesh);
    }
  }

  /**
   * Sets the cap this room enforces, and the margin before it — `GET /api/room-capacity`,
   * called once at boot, the same "fetched rather than hand-copied" reasoning `setCatalog`
   * already follows for the node types it offers. `null` (also the default before this is
   * ever called) hides the warning unconditionally.
   */
  setCapacity(capacity: RoomCapacityDto | null): void {
    this.capacity = capacity;
  }

  /**
   * Sits above the wheel — the topmost row `place` context has, now that mode switching no
   * longer needs a "pen" item there (plan/mode-hierarchy.md). `mesh` is passed explicitly
   * rather than read off `this.capacityWarningMesh`, since the one call site that builds a
   * fresh mesh (`rebuildCapacityWarningMesh`) needs to position it before assigning that field.
   */
  private positionCapacityWarning(mesh: Mesh): void {
    const topY = this.wheelY + WHEEL_ITEM_HEIGHT / 2;

    mesh.position.set(0, topY + CAPACITY_WARNING_GAP + CAPACITY_WARNING_HEIGHT / 2, 0);
  }

  /** Opens the tray above a hand; that hand cannot then take from it. */
  openAtHand(handedness: XRHandedness): void {
    this.anchor = { mode: 'hand', handedness };
    this.open = true;
    this.root.setEnabled(true);
    this.setPickable(false);
    this.resetNavigation();
  }

  /** Opens the tray floating in front of the viewer, for the desktop. */
  openInFront(): void {
    this.anchor = { mode: 'fixed' };
    this.open = true;
    this.root.setEnabled(true);
    this.positionTray();

    // A mouse reaches by picking, not by being somewhere. Only the fixed tray is made
    // pickable: in VR the slots must stay invisible to the aim ray, or the tray floating at
    // the off hand would steal aim from the room behind it.
    this.setPickable(true);
    this.resetNavigation();
  }

  toggleInFront(): void {
    if (this.open) {
      this.close();
      return;
    }

    this.openInFront();
  }

  close(): void {
    this.open = false;
    this.root.setEnabled(false);
    this.setPickable(false);
    this.resetNavigation();
  }

  start(): () => void {
    const observer = this.scene.onBeforeRenderObservable.add(() => this.tick(performance.now()));

    return () => this.scene.onBeforeRenderObservable.remove(observer);
  }

  private tick(nowMs: number): void {
    const dtMs = this.lastTickMs === null ? 16 : Math.min(nowMs - this.lastTickMs, 100);
    this.lastTickMs = nowMs;

    this.updateVisibility();

    if (!this.open) {
      return;
    }

    this.applyContext();
    this.positionTray();

    const dominant = this.grabbers.grabbers.filter((grabber) => grabber.role === 'dominant');
    const off = this.grabbers.byRole('off');

    // The grid the stick steps through, and what currently reads as "the one" — computed
    // before the reach-based checks below, since both the stick path and the reach path read
    // them, and neither owns the other.
    this.buildNavRows();
    this.clampCursor();
    this.tickStickNavigation(off, dominant);

    // A desktop click on a wheel — not a stick input, independent of tickStickNavigation,
    // which only ever runs for a controller.
    this.checkWheelDesktopClick(dominant);

    // Eases the category wheel toward its committed selection and repaints it every tick it is
    // showing — cheap, and `readout` has no wheel at all to ease (see this class's own
    // `TrayContext` doc). `Carousel.tick` does both the ease and the repaint in one call now —
    // there is no separate paint step left to run here.
    this.categoryWheel.tick(dtMs);

    this.refreshHighlightVisuals(off, dominant);

    if (this.showing === 'place') {
      this.checkForPick(dominant);
      this.updateCapacityWarning();
      return;
    }

    // The only context left once `place` is handled above.
    this.updateReadout();

    // Its own change-detection (`swatchKey`), independent of `updateReadout`'s own early
    // return above: a colour-only step can change the swatches while leaving the text lines
    // (e.g. "medium marker") completely unchanged, and the reverse is just as real (switching
    // to the eraser changes the line but clears the swatches to nothing) — neither call can be
    // skipped on the strength of the other's own comparison.
    this.updateReadoutSwatches();

    // Link mode's own line-style/arrow-direction carousels — independent of both calls above for
    // the identical reason: a style-only step can change this while the text lines and the
    // (empty, for Link) swatches stay exactly as they were.
    this.updateReadoutOptions();
  }

  /**
   * The off hand summons the *place* (Build) surface, and only the off hand — squeezing the
   * dominant hand's grip would produce a tray it then cannot take from. Every other mode's own
   * `readout` surface no longer opens this way at all: it belongs on the dominant hand now
   * (plan/mode-hierarchy.md, "A readout belongs on the hand that chooses it"), and
   * `updateReadoutVisibility` below is its own, separate visibility path — see that method's own
   * doc for why the grip-hold gesture does not carry over to it.
   *
   * Grip on a controller: a held gesture, so let go and the tray goes away, and there is no
   * mode to be stuck in.
   */
  private updateVisibility(): void {
    if (this.context() === 'readout') {
      this.updateReadoutVisibility();
      return;
    }

    const off = this.grabbers.byRole('off');
    const suppressed = this.openSuppressed();
    const wants = off !== undefined && !suppressed && off.near.pressed;

    if (off !== undefined && wants) {
      if (!this.open || this.anchor.mode !== 'hand' || this.anchor.handedness !== off.handedness) {
        this.openAtHand(off.handedness);
      }

      return;
    }

    this.transientReadoutUntilMs = null;

    // A fixed tray has no hand behind it, so it is left alone here: Tab opened it and Tab
    // closes it.
    if (this.open && this.anchor.mode === 'hand') {
      this.close();
    }
  }

  /**
   * Draw, Scan and Link's own `readout` surface — anchored to the *dominant* hand, the one that
   * operates the stick axis it reflects (plan/mode-hierarchy.md, "A readout belongs on the hand
   * that chooses it"; a headset pass found the old off-hand placement read as a bug: "The
   * carousel should move to the right hand for tool modes like that"). Build's own `place`
   * surface is unaffected — it stays off-hand, grip-held, in `updateVisibility` above.
   *
   * Opens **only** on a live `flashReadout()` window, never on a grip-hold: there is no dominant
   * grip gesture bound to "show the readout" any more, deliberately, because the dominant grip is
   * already spoken for by the mode ring (`ModeRingRenderer`, which appears above this same hand
   * for as long as that grip is held). Gating `flashing` on `dominant.near.pressed !== true` is
   * what actually keeps the two apart: `AuthoringController.tickToolCycle` only ever steps a
   * mode's own stick axis (and so only ever calls `flashReadout`) while the grip is *not* held —
   * grip-held turns the identical stick into the mode-switch gesture instead — but a flash can
   * still be mid-window at the exact moment the grip closes, and without this check that window
   * would keep this readout open, stacked on the mode ring, for whatever time was left on it.
   */
  private updateReadoutVisibility(): void {
    const dominant = this.grabbers.byRole('dominant');
    const suppressed = this.openSuppressed();

    const flashing =
      dominant !== undefined &&
      !suppressed &&
      dominant.near.pressed !== true &&
      this.transientReadoutUntilMs !== null &&
      performance.now() < this.transientReadoutUntilMs;

    if (flashing) {
      if (!this.open || this.anchor.mode !== 'hand' || this.anchor.handedness !== dominant.handedness) {
        this.openAtHand(dominant.handedness);
      }

      return;
    }

    this.transientReadoutUntilMs = null;

    if (this.open && this.anchor.mode === 'hand') {
      this.close();
    }
  }

  /**
   * Nudges the readout open for `TRANSIENT_READOUT_DURATION_MS`, anchored to the dominant hand —
   * see that constant's own doc for why this exists, and `updateReadoutVisibility` above for why
   * it is now the *only* way any of Draw/Scan/Link's readout ever opens (there is no grip-hold
   * path left for it to fall back to). `AuthoringController` calls this whenever a mode's own
   * dominant-stick axis actually changes something (`AxisBinding.step` returning non-null) —
   * every mode whose `offHandSurface` is `readout`, not Link alone: Draw and Scan have the
   * identical asymmetry (their own axes live on the dominant stick too), so a flash is the only
   * way any of the three would ever show feedback at all, now that none of them opens on a
   * grip-hold any more.
   *
   * Sets a deadline rather than a boolean, so a second call while one flash is already showing
   * simply extends it — spinning the stick several times in a row keeps the readout up
   * continuously rather than flickering closed and reopening between steps. Purely a request:
   * `updateReadoutVisibility` is what actually decides whether the tray opens, the same "the
   * tray's own contents do not change" contract every other context switch in this class already
   * follows — this only ever asks the existing readout content to appear on a timer, never a new
   * surface of its own.
   */
  flashReadout(): void {
    this.transientReadoutUntilMs = performance.now() + TRANSIENT_READOUT_DURATION_MS;
  }

  private applyContext(): void {
    const next = this.context();

    if (next === this.showing) {
      return;
    }

    this.showing = next;

    // The off hand can hold the tray open while the dominant hand grabs a real node directly,
    // which flips the context out from under it without the tray ever having closed. The set
    // of cells being navigated has completely changed, so this is the same "nothing selected"
    // start as opening the tray fresh — not a clamp of whatever the cursor used to point at.
    this.resetNavigation();

    this.placeGroup.setEnabled(next === 'place');
    this.readoutGroup.setEnabled(next === 'readout');

    // Forces a fresh build on the very next `updateReadout()` — see that method's own doc for
    // why comparing against the *previous* text (possibly from a different mode entirely) would
    // otherwise skip repainting a readout whose lines happen to coincide.
    if (next === 'readout') {
      this.readoutText = [];
      this.swatchKey = '';

      for (const entry of this.optionRowCarousels) {
        entry.key = '';
      }
    }
  }

  private positionTray(): void {
    const camera = this.scene.activeCamera;

    if (this.anchor.mode === 'hand') {
      const anchor = this.grabbers.byHandedness(this.anchor.handedness);

      if (!anchor) {
        return;
      }

      // Clear above the hand, measured to the lowest row. The hand holding a controller is
      // bigger than the grip pose suggests, and a tray that overlaps it is one you cannot see
      // to reach into.
      this.root.position.copyFrom(anchor.position.add(new Vector3(0, HAND_CLEARANCE, 0)));
    } else if (camera) {
      // Within arm's reach and slightly low, so it does not sit in front of the room.
      this.root.position.copyFrom(
        camera.globalPosition.add(camera.getForwardRay(1).direction.scale(0.55)).add(new Vector3(0, -0.15, 0)),
      );
    }

    // Yaw only: the tray should face the user, not tip with the wrist.
    //
    // Negated because CreatePlane's front face is -Z, so yawing +Z at the viewer shows the back
    // of every labelled slot and reads its text mirrored.
    if (camera) {
      const toCamera = camera.globalPosition.subtract(this.root.position);
      this.root.rotationQuaternion = Quaternion.FromEulerAngles(
        0,
        Math.atan2(-toCamera.x, -toCamera.z),
        0,
      );
    }
  }

  private checkForPick(dominant: Grabber[]): void {
    const active = this.categories.get(this.activeCategoryKey);

    if (!active) {
      return;
    }

    for (const grabber of dominant) {
      if (!grabber.far.justPressed) {
        continue;
      }

      for (const slot of active.slots) {
        // Forced — see CLAUDE.md, "An unforced computeWorldMatrix can return last frame's
        // answer": the tray is hand-anchored and repositioned earlier this same tick
        // (`positionTray`), so an unforced read here would judge the pick against where the
        // tray was last frame, not where it just moved to. `wheelItemUnderPointer` (this file,
        // below) already forces it for the identical reason; this was the omission, not that.
        slot.mesh.computeWorldMatrix(true);

        if (Vector3.Distance(grabber.position, slot.mesh.getAbsolutePosition()) > PICK_REACH) {
          continue;
        }

        this.close();
        this.onPicked?.(slot.selection, grabber);
        return;
      }
    }
  }

  /**
   * Shows or hides the room-capacity readout, and keeps its text current — called every tick
   * `place` context is showing, never gated on the tray having just opened, so a count that
   * crosses the threshold (or drops back under it, after a delete) while the tray is already
   * open is reflected live rather than only the next time the tray is opened fresh.
   *
   * Below the threshold this hides the mesh but does not dispose it — a room that hovers right
   * at the warning boundary (someone deleting and re-placing near node 90) would otherwise
   * rebuild the texture on every crossing in both directions instead of just toggling
   * visibility, which `rebuildCapacityWarningMesh` below already avoids for same-text ticks by
   * a different means (comparing the text itself). Both guards exist because they guard against
   * different repeated work: this one against rebuilding a mesh that was about to be hidden
   * anyway, that one against rebuilding a texture whose content has not actually changed.
   */
  private updateCapacityWarning(): void {
    if (this.capacity === null) {
      this.capacityWarningMesh?.setEnabled(false);
      return;
    }

    const count = this.nodeCount();
    const remaining = this.capacity.maxNodes - count;

    if (remaining > this.capacity.warningMargin) {
      this.capacityWarningMesh?.setEnabled(false);
      return;
    }

    const text = `${count} / ${this.capacity.maxNodes} nodes`;

    if (text !== this.capacityWarningText) {
      this.rebuildCapacityWarningMesh(text);
    }

    this.capacityWarningMesh?.setEnabled(true);
  }

  private rebuildCapacityWarningMesh(text: string): void {
    if (this.capacityWarningMesh) {
      disposePlate(this.capacityWarningMesh);
    }

    // Amber/red-leaning, deliberately unlike any node-category plate's own colour, so it
    // reads as a status readout rather than one more thing to pick.
    const mesh = this.buildLabelledPlate(
      'tray-capacity-warning',
      text,
      '#3A2333',
      '#FFB4C6',
      CAPACITY_WARNING_WIDTH,
      CAPACITY_WARNING_HEIGHT,
    );

    mesh.parent = this.placeGroup;
    mesh.isPickable = false;
    this.positionCapacityWarning(mesh);

    this.capacityWarningMesh = mesh;
    this.capacityWarningText = text;
  }

  /**
   * Rebuilds the `readout` context's own plates only when the lines actually changed — compared
   * by array content, not identity, since `ModeDefinition.readoutLines()` returns a fresh array
   * every call (`AuthoringController` wires it straight to the current mode's own function).
   * One plate per line, stacked vertically and centred, the same "grows away from the anchor"
   * layout the shape grid's own rows use.
   */
  private updateReadout(): void {
    const lines = this.readoutLines();

    if (arraysEqual(lines, this.readoutText)) {
      return;
    }

    this.readoutText = lines;

    for (const mesh of this.readoutMeshes) {
      disposePlate(mesh);
    }

    this.readoutMeshes = lines.map((line, index) => {
      const mesh = this.buildLabelledPlate(
        `tray-readout-${index}`,
        line,
        READOUT_BACKGROUND,
        READOUT_INK,
        READOUT_LINE_WIDTH,
        READOUT_LINE_HEIGHT,
      );

      mesh.parent = this.readoutGroup;
      mesh.isPickable = false;

      // Stacked upward from y = 0, first line on top — the same "reads top to bottom, last row
      // at the anchor height" convention `addCategory` already uses for the shape grid.
      const linesBelow = lines.length - 1 - index;
      mesh.position.set(0, linesBelow * READOUT_LINE_SPACING, 0);

      return mesh;
    });
  }

  /**
   * Rebuilds the `readout` context's own colour carousel, only when the swatch set actually
   * changed — compared by a flattened `color:selected` key rather than array identity, for the
   * identical reason `updateReadout` compares `readoutLines()` by content. Sits above the text
   * lines (`readoutMeshes`), so its anchor is repositioned from that row's own current height
   * every time — the two rows can change independently (a colour flick rebuilds only this one; a
   * variant flick rebuilds both), and this always re-anchors off whatever `readoutMeshes` most
   * recently settled to rather than assuming a fixed line count.
   *
   * Keyed by index, not by colour — `Carousel`'s own mesh names are `${prefix}-${item.key}`, and
   * indexing keeps them exactly `tray-readout-swatch-0`, `-1`, ... regardless of which colours
   * are showing, the same names this row used before it became a carousel. `snapTo` (not `goTo`)
   * is what actually selects the current one — see `Carousel.ts`'s own doc for why a readout
   * that only ever mirrors a decision made elsewhere (the dominant stick's colour axis) snaps to
   * it instead of spinning into place a second time.
   */
  private updateReadoutSwatches(): void {
    const swatches = this.readoutSwatches();
    const key = swatches.map((swatch) => `${swatch.color}:${swatch.selected}`).join('|');

    if (key !== this.swatchKey) {
      this.swatchKey = key;

      this.swatchCarousel.setItems(
        swatches.map((swatch, index) => ({ key: String(index), label: '', color: swatch.color })),
      );
    }

    if (swatches.length === 0) {
      return;
    }

    const topLineY = this.readoutMeshes.length > 0 ? (this.readoutMeshes.length - 1) * READOUT_LINE_SPACING : 0;
    const rowY = topLineY + READOUT_LINE_HEIGHT / 2 + SWATCH_ROW_GAP + SWATCH_SIZE / 2;
    this.swatchCarouselAnchor.position.set(0, rowY, 0);

    const selectedIndex = swatches.findIndex((swatch) => swatch.selected);

    if (selectedIndex >= 0) {
      this.swatchCarousel.snapTo(String(selectedIndex));
    }
  }

  /**
   * Rebuilds and repositions Link mode's own line-style/arrow-direction carousels
   * (`readoutOptionRows`) — the visual counterpart to the plain text `updateReadout` already
   * shows for the identical two values, stacked above both the text lines and (for every other
   * mode) the colour swatch row, in that order: row 0 sits highest, the same "index 0 reads
   * topmost" convention `updateReadout`'s own `linesBelow` already uses, so Link's line-style row
   * — the one carrying the destructive `'remove'` position — is the row nearest the eye.
   *
   * `optionRowCarousels` only ever grows, never shrinks: a row beyond what `rows` currently asks
   * for is hidden (`anchor.setEnabled(false)`), not disposed, the same "toggle visibility rather
   * than churn meshes" rule `capacityWarningMesh` already follows — switching between Link and
   * another readout mode while the tray stays open would otherwise rebuild carousels on every
   * mode switch. Every mode but Link returns zero rows, so the pool never grows past two today.
   *
   * Each row's own content is rebuilt (`Carousel.setItems`) only when its flattened
   * `key:label:selected:danger` content actually changes, then `snapTo`'d to whichever item is
   * marked `selected` — the identical two-step "rebuild, then snap" shape `updateReadoutSwatches`
   * already uses, for the identical reason: this only ever mirrors a decision the dominant stick
   * already made elsewhere, so there is nothing to visually ease *from*.
   */
  private updateReadoutOptions(): void {
    const rows = this.readoutOptionRows();

    while (this.optionRowCarousels.length < rows.length) {
      const index = this.optionRowCarousels.length;
      const anchor = new TransformNode(`tray-readout-option-row-${index}`, this.scene);
      anchor.parent = this.readoutGroup;

      const carousel = new Carousel(this.scene, anchor, {
        itemWidth: OPTION_ITEM_WIDTH,
        itemHeight: OPTION_ITEM_HEIGHT,
        ink: OPTION_INK,
        meshNamePrefix: `tray-readout-option-${index}`,
      });

      // See `setPickable`'s own doc — a carousel built here, mid-session, needs the tray's
      // *current* pickable state, not `Carousel`'s own `false` default.
      carousel.setPickable(this.pickableState);

      this.optionRowCarousels.push({ anchor, carousel, key: '' });
    }

    // The text block's own top edge — the identical formula `updateReadoutSwatches` already uses
    // for the swatch row's own anchor, so a Link-mode readout with option rows stacks exactly the
    // way every other readout's swatch row already does above its own text.
    const textTopY = this.readoutMeshes.length > 0 ? (this.readoutMeshes.length - 1) * READOUT_LINE_SPACING : 0;
    const baseY = textTopY + READOUT_LINE_HEIGHT / 2;

    this.optionRowCarousels.forEach((entry, index) => {
      const row = rows[index];

      if (!row) {
        entry.anchor.setEnabled(false);
        return;
      }

      entry.anchor.setEnabled(true);

      const key = row
        .map((option) => `${option.key}:${option.label}:${option.selected}:${option.danger ?? false}`)
        .join('|');

      if (key !== entry.key) {
        entry.key = key;

        entry.carousel.setItems(
          row.map((option) => ({
            key: option.key,
            label: option.label,
            color: option.danger ? OPTION_DANGER_BACKGROUND : OPTION_BACKGROUND,
          })),
        );
      }

      const rowsBelow = rows.length - 1 - index;
      const y = baseY + OPTION_ROW_GAP + OPTION_ITEM_HEIGHT / 2 + rowsBelow * (OPTION_ITEM_HEIGHT + OPTION_ROW_GAP);
      entry.anchor.position.set(0, y, 0);

      const selected = row.find((option) => option.selected);

      if (selected) {
        entry.carousel.snapTo(selected.key);
      }
    });
  }

  private showCategory(category: string): void {
    this.activeCategoryKey = category;

    for (const [key, group] of this.categories) {
      group.row.setEnabled(key === category);
    }
  }

  // --- off-hand stick navigation ----------------------------------------------------------

  /** Whichever wheel `this.showing` implies, or null in a context with no *stick/pointer-driven*
   *  wheel at all — `place`'s own category wheel is the only one left navigable this way;
   *  `readout`'s colour carousel is driven externally instead (`updateReadoutSwatches`, snapped
   *  to whatever the dominant stick's own axis already picked), not by the off-hand stick or the
   *  desktop pointer, so it is not one of the wheels this method ever returns (see this class's
   *  own `TrayContext` doc). */
  private wheelForCurrentContext(): Carousel | null {
    return this.showing === 'place' ? this.categoryWheel : null;
  }

  /**
   * The grid the stick steps through, rebuilt from whatever is actually on screen. Row 0 is
   * the wheel's own row in the place context; `readout` has nothing navigable at all, so it is
   * empty — a stick push in that context does nothing here, leaving it free for `XrManager`'s
   * own teleport/snap-turn instead (plan/mode-hierarchy.md).
   */
  private buildNavRows(): void {
    if (this.showing === 'place') {
      const rows: NavCell[][] = [[{ kind: 'wheel' }]];

      const active = this.categories.get(this.activeCategoryKey);

      if (active) {
        const lines = Math.ceil(active.slots.length / SLOTS_PER_ROW);

        for (let line = 0; line < lines; line += 1) {
          const row: NavCell[] = [];

          for (let column = 0; column < SLOTS_PER_ROW; column += 1) {
            const index = line * SLOTS_PER_ROW + column;

            if (index >= active.slots.length) {
              break;
            }

            row.push({ kind: 'slot', category: this.activeCategoryKey, index });
          }

          rows.push(row);
        }
      }

      this.navRows = rows;
      return;
    }

    // `readout` — the only other context — has nothing navigable at all.
    this.navRows = [];
  }

  /**
   * Keeps the cursor inside `navRows` after it changes shape, and resolves a pending "focus
   * the wheel" reset (`cursorWantsWheelFocus`) against the rows this same tick just built —
   * see that field's own doc for why the resolution has to happen here rather than at the
   * moment of the reset itself.
   */
  private clampCursor(): void {
    if (this.cursorWantsWheelFocus) {
      this.cursorWantsWheelFocus = false;

      const wheelRow = this.navRows.findIndex((row) => row.length === 1 && row[0]!.kind === 'wheel');
      this.cursor = wheelRow >= 0 ? { row: wheelRow, col: 0 } : null;
      return;
    }

    if (this.cursor === null) {
      return;
    }

    if (this.navRows.length === 0) {
      this.cursor = null;
      return;
    }

    const row = clampNumber(this.cursor.row, 0, this.navRows.length - 1);
    const cells = this.navRows[row] ?? [];

    if (cells.length === 0) {
      this.cursor = null;
      return;
    }

    this.cursor = { row, col: clampNumber(this.cursor.col, 0, cells.length - 1) };
  }

  private cellAt(cursor: TrayCursor | null): NavCell | null {
    if (cursor === null) {
      return null;
    }

    return this.navRows[cursor.row]?.[cursor.col] ?? null;
  }

  /**
   * Reads the off-hand stick and steps the cursor, then checks the trigger. A grabber with no
   * stick — no off hand present, a tracked hand, or the desktop pointer — leaves the cursor
   * untouched, which is also its state before the stick has ever been moved.
   */
  private tickStickNavigation(off: Grabber | undefined, dominant: Grabber[]): void {
    if (!off || off.stick === null) {
      return;
    }

    this.stepCursor(off.stick);
    this.tickFire(off, dominant);
  }

  private stepCursor(stick: { x: number; y: number }): void {
    const magnitudeX = Math.abs(stick.x);
    const magnitudeY = Math.abs(stick.y);

    if (magnitudeX < STEP_RELEASE && magnitudeY < STEP_RELEASE) {
      this.stickArmed = true;
    }

    if (!this.stickArmed || (magnitudeX < STEP_ENGAGE && magnitudeY < STEP_ENGAGE)) {
      return;
    }

    this.stickArmed = false;

    // The bigger axis wins a diagonal push, so it reads as one clean step rather than an
    // ambiguous mix of two. WebXR's thumbstick y axis is negative-forward, the same convention
    // an ordinary gamepad uses, so pushing away from you — "up" — is negative.
    let direction: 'up' | 'down' | 'left' | 'right';

    if (magnitudeX >= magnitudeY) {
      direction = stick.x > 0 ? 'right' : 'left';
    } else {
      direction = stick.y > 0 ? 'down' : 'up';
    }

    this.moveCursor(direction);
  }

  /**
   * Moves the highlight one cell — or, if the cursor is on the wheel's own row, spins the
   * wheel by one item instead of moving `col` at all (a wheel row is always exactly one cell
   * wide, so there is nothing for `col` to do there). `place` never sees the "reveal from
   * nothing" branch below in practice, because its cursor already starts on the wheel
   * (`resetNavigation`) — `readout` has no navigable rows at all, so nothing ever reaches this
   * with a null cursor in practice either; kept for the general case rather than assumed away.
   */
  private moveCursor(direction: 'up' | 'down' | 'left' | 'right'): void {
    if (this.cursor === null) {
      this.cursor = { row: 0, col: 0 };
      this.clampCursor();
      return;
    }

    const { row, col } = this.cursor;

    if (direction === 'left' || direction === 'right') {
      const cells = this.navRows[row] ?? [];
      const cell = cells[0];

      if (cells.length === 1 && cell?.kind === 'wheel') {
        const wheel = this.wheelForCurrentContext();
        const changed = wheel?.stepBy(direction === 'right' ? 1 : -1) ?? false;

        if (changed) {
          this.dispatchWheelChange();
        }

        return;
      }

      this.cursor = {
        row,
        col: clampNumber(col + (direction === 'right' ? 1 : -1), 0, cells.length - 1),
      };
      return;
    }

    const nextRow = clampNumber(row + (direction === 'down' ? 1 : -1), 0, this.navRows.length - 1);
    const nextCells = this.navRows[nextRow] ?? [];
    this.cursor = { row: nextRow, col: clampNumber(col, 0, nextCells.length - 1) };
  }

  /** Applies the category wheel's active item — the only wheel left (see this class's own
   *  `TrayContext` doc) — purely internally, no callback. Called the instant a wheel step or
   *  drag crosses into a new item. */
  private dispatchWheelChange(): void {
    if (this.showing === 'place') {
      this.showCategory(this.categoryWheel.activeItem?.key ?? '');
    }
  }

  /**
   * The trigger fires whatever is highlighted — a slot, the only kind of cell left to fire. A
   * wheel cell is inert here: "the trigger is not read while the wheel has focus"
   * (plan/tray-categories.md) — the change already happened as the stick spun it, so there is
   * nothing left to confirm.
   */
  private tickFire(off: Grabber, dominant: Grabber[]): void {
    const cell = this.cellAt(this.cursor);

    if (cell === null || cell.kind === 'wheel' || !off.far.justPressed) {
      return;
    }

    this.fire(cell, dominant);
  }

  /**
   * Carries out whatever is highlighted. A slot hands the new node to the *dominant* grabber to
   * carry, not the off hand that fired it: the off hand is busy holding the grip that keeps the
   * tray open.
   */
  private fire(cell: NavCell, dominant: Grabber[]): void {
    if (cell.kind === 'wheel') {
      return; // tickFire already short-circuits this; kept for exhaustiveness.
    }

    const slot = this.categories.get(cell.category)?.slots[cell.index];
    const grabber = dominant[0];

    if (!slot || !grabber) {
      return;
    }

    this.close();
    this.onPicked?.(slot.selection, grabber);
  }

  private resetNavigation(): void {
    this.stickArmed = true;
    this.cursor = null;
    this.cursorWantsWheelFocus = this.showing === 'place';
  }

  // --- the desktop pointer on a wheel --------------------------------------------------------

  /** The visible wheel item currently under the desktop pointer's own cursor position, or null
   *  with no hit — a real reach test against each item's own mesh, never a projection, per
   *  plan/tray-categories.md's own "the depth rule": an unpicked cursor sitting far along its
   *  gaze ray must not register, and it will not, because it never lands within WHEEL_REACH of
   *  any wheel item's actual, forced-fresh world position. */
  private wheelItemUnderPointer(pointer: Grabber, wheel: Carousel): string | null {
    for (const slot of wheel.slots()) {
      const mesh = wheel.meshFor(slot.item.key);

      if (!mesh || !mesh.isEnabled()) {
        continue;
      }

      mesh.computeWorldMatrix(true);

      if (Vector3.Distance(pointer.position, mesh.getAbsolutePosition()) <= WHEEL_REACH) {
        return slot.item.key;
      }
    }

    return null;
  }

  /** Clicking a flanking item rotates it to the front; clicking the front item does nothing,
   *  because it is already active (`TrayWheel.goTo` is already a no-op for that case). */
  private checkWheelDesktopClick(dominant: Grabber[]): void {
    const wheel = this.wheelForCurrentContext();

    if (!wheel) {
      return;
    }

    for (const grabber of dominant) {
      if (grabber.kind !== 'pointer' || !grabber.far.justPressed) {
        continue;
      }

      const key = this.wheelItemUnderPointer(grabber, wheel);

      if (key === null) {
        continue;
      }

      if (wheel.goTo(key)) {
        this.dispatchWheelChange();
      }

      return;
    }
  }

  /**
   * One scroll notch spins whichever wheel is showing by one item, but only while the pointer
   * is genuinely hovering one of its items — otherwise scrolling over the open room must not
   * change anything (the same "aiming at the room" hazard the depth rule guards against for
   * the hand's capture sphere, in its desktop-scroll form). Called by DevSim from a real
   * `wheel` DOM event; a no-op with the tray closed, or in a context with no wheel at all.
   */
  scrollWheel(deltaY: number): void {
    if (!this.open) {
      return;
    }

    const wheel = this.wheelForCurrentContext();

    if (!wheel) {
      return;
    }

    const pointer = this.grabbers.grabbers.find((grabber) => grabber.kind === 'pointer');

    if (!pointer) {
      return;
    }

    if (this.wheelItemUnderPointer(pointer, wheel) === null) {
      return;
    }

    if (wheel.stepBy(deltaY > 0 ? 1 : -1)) {
      this.dispatchWheelChange();
    }
  }

  // --- highlight visuals -------------------------------------------------------------------

  /**
   * What reads as "this is the one" for slots. A controller's off hand drives it by stick, so
   * the highlight follows the cursor exclusively; hands and the desktop pointer have no stick,
   * so for them this is proximity instead. The wheel paints itself separately
   * (`Carousel.tick`/`Carousel.snapTo`) and unconditionally — it has no "about to be chosen"
   * state distinct from "currently chosen," so nothing here needs to decide whether it is
   * highlighted.
   */
  private refreshHighlightVisuals(off: Grabber | undefined, dominant: Grabber[]): void {
    if (this.showing !== 'place') {
      // `readout` has nothing to highlight at all — no dwell-fireable or pick-fireable content.
      return;
    }

    const highlighted = off && off.stick !== null ? this.cellAt(this.cursor) : this.hoverCell(dominant);
    this.paintSlots(highlighted);
  }

  /**
   * Proximity only, no press required — the hover-preview counterpart of the pick check below,
   * which still requires one. Only ever called while `place` is showing — `readout` has
   * nothing to hover.
   */
  private hoverCell(dominant: Grabber[]): NavCell | null {
    const active = this.categories.get(this.activeCategoryKey);

    if (!active) {
      return null;
    }

    for (const grabber of dominant) {
      for (let index = 0; index < active.slots.length; index += 1) {
        const slot = active.slots[index];

        // Forced for the same reason `checkForPick` above forces it — a hand-anchored tray
        // repositioned earlier this same tick, read here unforced, judges hover against last
        // frame's position rather than this one's.
        slot?.mesh.computeWorldMatrix(true);

        if (slot && Vector3.Distance(grabber.position, slot.mesh.getAbsolutePosition()) <= PICK_REACH) {
          return { kind: 'slot', category: this.activeCategoryKey, index };
        }
      }
    }

    return null;
  }

  private paintSlots(highlighted: NavCell | null): void {
    const active = this.categories.get(this.activeCategoryKey);

    if (!active) {
      return;
    }

    active.slots.forEach((slot, index) => {
      const isHighlighted =
        highlighted?.kind === 'slot' &&
        highlighted.category === this.activeCategoryKey &&
        highlighted.index === index;

      slot.mesh.scaling.setAll(MINI_SCALE * (isHighlighted ? HIGHLIGHT_SCALE : 1));
      slot.mesh.position.z = isHighlighted ? HIGHLIGHT_FORWARD : 0;
    });
  }

  private addCategory(
    category: string,
    entries: {
      kind: NodeKindValue;
      shapeType?: string;
      color: string;
      silhouette: string;
      displayName: string;
    }[],
  ): void {
    const row = new TransformNode(`tray-row-${category}`, this.scene);
    row.parent = this.placeGroup;
    row.setEnabled(false);

    const slots: Slot[] = entries.map((entry, index) => {
      const mesh = this.buildMini(entry.silhouette, entry.color, `${category}-${index}`, entry.shapeType);

      // Wrapped into rows of five: eight in a line is wider than a comfortable reach, and the
      // ends of a long row are exactly where a tray stops being usable.
      const column = index % SLOTS_PER_ROW;
      const line = Math.floor(index / SLOTS_PER_ROW);
      const inThisLine = Math.min(SLOTS_PER_ROW, entries.length - line * SLOTS_PER_ROW);
      const lines = Math.ceil(entries.length / SLOTS_PER_ROW);

      // Stacked upward with the first row on top, so reading order is still top-to-bottom while
      // the last row stays at y = 0 — the height the tray's clearance above the hand is measured
      // from. Laying rows downward is what buried them in the hand.
      mesh.position.set(
        (column - (inThisLine - 1) / 2) * SLOT_SPACING,
        (lines - 1 - line) * ROW_SPACING,
        0,
      );

      mesh.parent = row;
      mesh.isPickable = false;
      mesh.metadata = { trayPick: true };

      return {
        mesh,
        selection: {
          kind: entry.kind,
          shapeType: entry.shapeType,
          color: entry.color,
          displayName: entry.displayName,
        },
      };
    });

    this.categories.set(category, { row, slots });
  }

  /**
   * A miniature of the real thing.
   *
   * A model-backed type's mini is cloned from the exact template a real node of that type
   * would clone (NodeMeshFactory.createShape makes the identical choice), so what you take off
   * the tray is recognisably what lands in your hand — a preview built from a *different* shape
   * than the real thing would be worse than no preview, per plan/diagramming.md. Everything
   * else still falls back to the procedural silhouette factory exactly as before: a shape whose
   * model failed to load, and every type that was never model-backed to begin with, look
   * identical here, the same "degrade rather than throw" rule an unrecognised silhouette
   * already followed.
   *
   * `cloneModelTemplate` is the exact function NodeMeshFactory.createShape uses for a real
   * node — same clone-then-unparent-then-reenable steps, so this needs no separate reasoning
   * about the two `Mesh.clone` gotchas CLAUDE.md records (a `null` newParent not clearing the
   * source's own parent; a clone inheriting the disabled/unpickable template's own state).
   */
  private buildMini(silhouette: string, color: string, id: string, shapeType?: string): Mesh {
    const name = `tray-mini-${id}`;
    const template = shapeType ? this.modelTemplateFor(shapeType) : undefined;

    const mesh = template ? cloneModelTemplate(template, name) : buildSilhouette(silhouette, this.scene);

    mesh.name = name;

    // MINI_SCALE is the tray's own scale, unrelated to NodeMeshFactory's TARGET_SIZE/0.3m node
    // convention: a model template is already baked to that 0.3m convention (ModelLibrary,
    // "Centred and normalised to 0.3m"), exactly like a procedural silhouette is already
    // authored at Silhouettes.ts's own SIZE — so a template-backed mini scales down by the same
    // MINI_SCALE factor as every other mini, with no special case here.
    mesh.scaling.setAll(MINI_SCALE);

    // Independent from the template's own material for the same reason a real node's is:
    // ModelLibrary discards the imported material once the template is built, and every clone —
    // node or tray mini — gets a fresh StandardMaterial of its own here, so fading one instance
    // (a held node, or, in principle, a mini) can never affect another.
    const material = new StandardMaterial(`tray-${id}`, this.scene);
    const tint = Color3.FromHexString(color);
    material.diffuseColor = tint;

    // Lit from within, so the tray reads clearly regardless of where the user is standing.
    material.emissiveColor = tint.scale(0.45);
    material.specularColor = Color3.Black();
    mesh.material = material;

    return mesh;
  }

  /**
   * A labelled card, rounded rather than a hard-edged block — see the module-level
   * `buildLabelledPlate` this delegates to for the full account of why. Kept as a private
   * method too, rather than replacing every call site with the free function directly, so
   * nothing else about this class's own construction calls has to change.
   */
  private buildLabelledPlate(
    name: string,
    label: string,
    background: string,
    ink: string,
    width: number,
    height: number,
  ): Mesh {
    return buildLabelledPlate(this.scene, name, label, background, ink, width, height);
  }

  /**
   * `categoryWheel`/`swatchCarousel` used to be reached into directly here (via `everyMesh()`'s
   * own `allMeshes()` yields), bypassing `Carousel.setPickable` entirely — leaving two owners
   * for one flag: this loop set `isPickable` once, at call time, and `Carousel.rebuildMeshes`
   * separately hardcoded every *freshly built* mesh's `isPickable` back to `false` regardless,
   * silently reverting whatever this had just set the moment either carousel's own `setItems`
   * next ran (`updateReadoutSwatches` calls `swatchCarousel.setItems` on every tick `readout`
   * context is showing, so this could happen within the same session, not only across a
   * reload). Delegating to each carousel's own `setPickable` instead makes it the one place
   * that both applies the flag *and* remembers it for the next rebuild.
   */
  private setPickable(pickable: boolean): void {
    // Remembered so a row carousel `updateReadoutOptions` grows into the pool later, mid-session,
    // can be given the tray's *current* pickable state at construction time — the identical
    // "two owners for one flag" defect this method's own doc records for `Carousel.setPickable`
    // itself, one level up: without this, a carousel built after the last `setPickable` call
    // would default to `Carousel`'s own `false` regardless of what the fixed desktop tray had
    // already turned on.
    this.pickableState = pickable;

    for (const group of this.categories.values()) {
      for (const slot of group.slots) {
        slot.mesh.isPickable = pickable;
      }
    }

    for (const mesh of this.readoutMeshes) {
      mesh.isPickable = pickable;
    }

    this.categoryWheel.setPickable(pickable);
    this.swatchCarousel.setPickable(pickable);

    for (const entry of this.optionRowCarousels) {
      entry.carousel.setPickable(pickable);
    }
  }

  private clearCategories(): void {
    for (const group of this.categories.values()) {
      for (const slot of group.slots) {
        disposePlate(slot.mesh);
      }

      group.row.dispose();
    }

    this.categories.clear();
  }

  dispose(): void {
    this.clearCategories();
    this.categoryWheel.dispose();

    for (const mesh of this.readoutMeshes) {
      disposePlate(mesh);
    }

    this.readoutMeshes = [];
    this.readoutText = [];

    this.swatchCarousel.dispose();
    this.swatchKey = '';

    for (const entry of this.optionRowCarousels) {
      entry.carousel.dispose();
      entry.anchor.dispose();
    }

    this.optionRowCarousels.length = 0;

    if (this.capacityWarningMesh) {
      disposePlate(this.capacityWarningMesh);
      this.capacityWarningMesh = null;
    }

    this.root.dispose();
  }
}

function arraysEqual(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

function clampNumber(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}
